/**
 * ユーザー詳細ページ
 * ラクハイ管理者が全ユーザーの詳細情報を確認・削除するページ
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TrashIcon,
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { SuperAdminService, UserProfile } from '../../services/superAdminService';
import PageTitle from '../../components/ui/PageTitle';
import Button from '../../components/ui/Button';
import { DeleteConfirmModal } from '../../components/ui/DeleteConfirmModal';
import { getRoleLabel, getRoleColor } from '../../utils/roleUtils';
import logger from '../../utils/logger';

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const superAdminService = new SuperAdminService();

  useEffect(() => {
    loadUserDetail();
  }, [userId]);

  const loadUserDetail = async () => {
    if (!userId) {
      navigate('/admin/all-users');
      return;
    }

    try {
      setLoading(true);
      const users = await superAdminService.getAllUsers();
      const targetUser = users.find(u => u.id === userId);

      if (!targetUser) {
        logger.error('ユーザーが見つかりません:', userId);
        navigate('/admin/all-users');
        return;
      }

      setUser(targetUser);
    } catch (error) {
      logger.error('ユーザー詳細取得エラー:', error);
      navigate('/admin/all-users');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!userId || !user) return;

    try {
      setDeleting(true);
      await superAdminService.deleteUser(userId);
      logger.info(`✅ ユーザー削除成功: ${user.name}`);
      navigate('/admin/all-users');
    } catch (error) {
      logger.error('ユーザー削除エラー:', error);
      alert('ユーザーの削除に失敗しました。');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 p-6'>
        <div className='max-w-4xl mx-auto'>
          <div className='text-center text-gray-500 dark:text-gray-400'>読み込み中...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const isSuperAdmin = user.role === 'super_admin';

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900 p-6'>
      <div className='max-w-4xl mx-auto'>
        {/* ヘッダー */}
        <div className='mb-6'>
          <Button
            variant='ghost'
            onClick={() => navigate('/admin/all-users')}
            className='mb-4 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
          >
            <ArrowLeftIcon className='h-5 w-5 mr-2' />
            ユーザー一覧に戻る
          </Button>
          <PageTitle icon={UserIcon} title='ユーザー詳細' />
        </div>

        {/* メインカード */}
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden'>
          {/* 基本情報 */}
          <div className='p-6'>
            <div className='flex items-center justify-between mb-6'>
              <h3 className='text-xl font-bold text-gray-800 dark:text-white'>{user.name}</h3>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}
              >
                {getRoleLabel(user.role)}
              </span>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
              {/* メールアドレス */}
              <div className='flex items-center text-gray-600 dark:text-gray-300'>
                <EnvelopeIcon className='h-4 w-4 mr-2 flex-shrink-0' />
                <span className='text-sm'>{user.email}</span>
              </div>

              {/* 所属会社 */}
              <div className='flex items-center text-gray-600 dark:text-gray-300'>
                <BuildingOfficeIcon className='h-4 w-4 mr-2 flex-shrink-0' />
                <span className='text-sm'>{user.company_name || '未設定'}</span>
              </div>

              {/* 作成日時 */}
              <div className='flex items-center text-gray-600 dark:text-gray-300'>
                <CalendarDaysIcon className='h-4 w-4 mr-2 flex-shrink-0' />
                <span className='text-sm'>{new Date(user.created_at).toLocaleString('ja-JP')}</span>
              </div>
            </div>
          </div>

          {/* システム情報 */}
          <div className='px-6 pb-6'>
            <div className='bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4'>
              <h3 className='text-sm font-medium text-gray-900 dark:text-white mb-3'>
                システム情報
              </h3>
              <div className='text-sm'>
                <div>
                  <span className='text-gray-500 dark:text-gray-400'>ユーザーID: </span>
                  <span className='text-gray-900 dark:text-white font-mono'>{user.id}</span>
                </div>
              </div>
            </div>
          </div>

          {/* アクションボタン */}
          <div className='flex justify-end gap-3 px-6 pb-6 border-t border-gray-200 dark:border-gray-700 pt-6'>
            {!isSuperAdmin && (
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant='danger'
                size='md'
                disabled={deleting}
                className='flex items-center gap-2'
              >
                <TrashIcon className='h-4 w-4' />
                {deleting ? '削除中...' : '削除'}
              </Button>
            )}
            {isSuperAdmin && (
              <p className='text-sm text-gray-500 dark:text-gray-400 italic py-2'>
                ラクハイ管理者アカウントは削除できません
              </p>
            )}
          </div>
        </div>

        {/* 削除確認モーダル */}
        <DeleteConfirmModal
          isOpen={showDeleteConfirm}
          itemName={user.name}
          itemType='ユーザー'
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          isDeleting={deleting}
        />
      </div>
    </div>
  );
};

export default UserDetailPage;
